import { parseColor } from '@color';
import { ObjectFit } from '@image';
import { z } from 'zod';
import type { ElementId } from './types';
import { BasicTextStyle } from '@typography';
import { IStringSchema } from '@strings';

// Enhanced color schema that supports fg- prefixed colors
export const SnorfColorSchema = z.unknown().transform(input => parseColor(input));

// Core primitive schemas
export const ElementIdSchema = z.string().transform(id => id as ElementId);

export const PaddingSchema = z.union([
  // Full specification
  z
    .object({
      leading: z.number().min(0).default(0),
      trailing: z.number().min(0).default(0),
      top: z.number().min(0).default(0),
      bottom: z.number().min(0).default(0)
    })
    .partial()
    .transform(data => ({
      leading: data.leading ?? 0,
      trailing: data.trailing ?? 0,
      top: data.top ?? 0,
      bottom: data.bottom ?? 0
    })),
  // Alias: number -> uniform padding
  z
    .number()
    .min(0)
    .transform(padding => ({
      leading: padding,
      trailing: padding,
      top: padding,
      bottom: padding
    }))
]);

export const CornerRadiusSchema = z.union([
  // Full specification
  z
    .object({
      topLeft: z.number().min(0).default(0),
      topRight: z.number().min(0).default(0),
      bottomLeft: z.number().min(0).default(0),
      bottomRight: z.number().min(0).default(0)
    })
    .partial()
    .transform(data => ({
      topLeft: data.topLeft ?? 0,
      topRight: data.topRight ?? 0,
      bottomLeft: data.bottomLeft ?? 0,
      bottomRight: data.bottomRight ?? 0
    })),
  // Alias: number -> uniform corner radius
  z
    .number()
    .min(0)
    .transform(radius => ({
      topLeft: radius,
      topRight: radius,
      bottomLeft: radius,
      bottomRight: radius
    }))
]);

export const SizingMinMaxSchema = z.union([
  // Full specification
  z
    .object({
      min: z.number().min(0),
      max: z.number().min(0)
    })
    .refine(data => data.min <= data.max, {
      message: 'Sizing min must be <= max',
      path: ['min']
    }),
  // Partial specification with defaults
  z
    .object({
      min: z.number().min(0).default(0),
      max: z.number().min(0).default(Number.MAX_VALUE)
    })
    .partial()
    .transform(data => ({
      min: data.min ?? 0,
      max: data.max ?? Number.MAX_VALUE
    }))
    .refine(data => data.min <= data.max, {
      message: 'Sizing min must be <= max',
      path: ['min']
    })
]);

// Enum schemas
export const SizingTypeSchema = z.enum(['fit', 'grow', 'percent', 'fixed']);
export const LayoutDirectionSchema = z.enum([
  'left-to-right',
  'right-to-left',
  'top-to-bottom'
]);
export const LayoutAlignmentXSchema = z.enum(['left', 'center', 'right']);
export const LayoutAlignmentYSchema = z.enum(['top', 'center', 'bottom']);

// SizingAxis schema with aliases
export const SizingAxisSchema = z.union([
  // Full specification
  z.object({
    type: SizingTypeSchema,
    size: z.union([
      SizingMinMaxSchema,
      z.number().min(0).max(1), // for percent
      z.number().min(0) // for fixed (will be converted to SizingMinMax)
    ])
  }),
  // Alias: string -> { type: string, size: default }
  z.enum(['fit', 'grow']).transform(type => ({
    type,
    size: { min: 0, max: Number.MAX_VALUE }
  })),
  // Alias: number -> { type: 'fixed', size: number }
  z
    .number()
    .min(0)
    .transform(size => ({
      type: 'fixed' as const,
      size: { min: size, max: size }
    }))
]);

// Sizing schema with aliases (simplified - only needed for object-style width/height properties)
export const SizingSchema = z.object({
  width: SizingAxisSchema,
  height: SizingAxisSchema
});

// ChildAlignment schema
export const ChildAlignmentSchema = z
  .object({
    x: LayoutAlignmentXSchema.default('left'),
    y: LayoutAlignmentYSchema.default('top')
  })
  .partial()
  .transform(data => ({
    x: data.x ?? 'left',
    y: data.y ?? 'top'
  }));

// LayoutConfig schema - simplified to only support flat width/height properties
export const LayoutConfigSchema = z
  .object({
    width: z.union([SizingAxisSchema, z.number().min(0)]).optional(),
    height: z.union([SizingAxisSchema, z.number().min(0)]).optional(),
    padding: PaddingSchema.optional(),
    childGap: z.number().min(0).optional(),
    direction: LayoutDirectionSchema.optional(),
    childAlignment: ChildAlignmentSchema.optional()
  })
  .transform(data => {
    // Build sizing from direct width/height properties
    const sizing = {
      width:
        data.width !== undefined
          ? typeof data.width === 'number'
            ? {
                type: 'fixed' as const,
                size: { min: data.width, max: data.width }
              }
            : data.width
          : { type: 'fit' as const, size: { min: 0, max: Number.MAX_VALUE } },
      height:
        data.height !== undefined
          ? typeof data.height === 'number'
            ? {
                type: 'fixed' as const,
                size: { min: data.height, max: data.height }
              }
            : data.height
          : { type: 'fit' as const, size: { min: 0, max: Number.MAX_VALUE } }
    };

    return {
      sizing,
      padding: data.padding ?? { leading: 0, trailing: 0, top: 0, bottom: 0 },
      childGap: data.childGap ?? 0,
      direction: data.direction ?? 'left-to-right',
      childAlignment: data.childAlignment ?? { x: 'left', y: 'top' }
    };
  });

// Border schemas
export const RectBordersSchema = z.union([
  // Full specification
  z
    .object({
      left: z.number().min(0).default(0),
      right: z.number().min(0).default(0),
      top: z.number().min(0).default(0),
      bottom: z.number().min(0).default(0)
    })
    .partial()
    .transform(data => ({
      left: data.left ?? 0,
      right: data.right ?? 0,
      top: data.top ?? 0,
      bottom: data.bottom ?? 0
    })),
  // Alias: number -> uniform border width
  z
    .number()
    .min(0)
    .transform(width => ({
      left: width,
      right: width,
      top: width,
      bottom: width
    }))
]);

export const BetweenChildBordersSchema = z.object({
  betweenChildren: z.number().min(0)
});

export const BorderWidthSchema = z.union([BetweenChildBordersSchema, RectBordersSchema]);

// FontSizeChooser schemas
export const FixedFontSizeChooserSchema = z.object({
  type: z.literal('fixed'),
  fontSize: z.number().min(0)
});

export const ScalingFontSizeChooserSchema = z.object({
  type: z.literal('scaling'),
  fontSizeRange: z.tuple([z.number().min(0), z.number().min(0)]),
  taperCharCountRange: z.tuple([z.number().min(0), z.number().min(0)])
});

export const FontSizeChooserSchema = z.union([
  FixedFontSizeChooserSchema,
  ScalingFontSizeChooserSchema
]);

// LayoutTextOptions schema
const RawLayoutTextOptionsSchema = z
  .object({
    fontSizeChooser: FontSizeChooserSchema.optional(),
    minWidth: z.number().min(0).optional(),
    maxWidth: z.number().min(0).optional(),
    aspectRatio: z.number().min(0).optional(),
    maxLines: z.number().min(1).optional(),
    basicTextStyle: z.nativeEnum(BasicTextStyle).optional(),
    // Alias: 'style' -> basicTextStyle
    style: z.nativeEnum(BasicTextStyle).optional()
  })
  .partial()
  .transform(data => ({
    ...data,
    basicTextStyle: data.basicTextStyle ?? data.style
  }))
  .optional();

export const LayoutTextOptionsSchema = RawLayoutTextOptionsSchema;

// BasicTextStyle schema for SNORF DSL (lowercase-hyphenated strings)
// no separate top-level style; use layoutOptions.basicTextStyle

export const BorderElementConfigSchema = z.union([
  // Alias: number -> { width: { left: n, right: n, top: n, bottom: n } }
  z
    .number()
    .min(0)
    .transform(n => ({
      width: { left: n, right: n, top: n, bottom: n }
    })),
  // Explicit form
  z
    .object({
      color: SnorfColorSchema.optional(),
      width: BorderWidthSchema.optional()
    })
    .partial()
    .transform(data => ({
      color: data.color,
      width: (() => {
        const defaultWidth = { left: 0, right: 0, top: 0, bottom: 0 };
        if (!data.width) return defaultWidth;
        if ('betweenChildren' in data.width) return data.width;
        // Merge partial rect borders with defaults
        return { ...defaultWidth, ...data.width };
      })()
    }))
]);

// TextElementData schema
const RawTextElementDataSchema = z
  .object({
    text: IStringSchema,
    color: SnorfColorSchema,
    // Alias: top-level 'style' convenience; maps to layoutOptions.basicTextStyle
    style: z.nativeEnum(BasicTextStyle).optional(),
    layoutOptions: LayoutTextOptionsSchema,
    measured: z.any().optional() // MeasuredText - will need proper schema later
  })
  .transform(data => ({
    ...data,
    layoutOptions: {
      ...data.layoutOptions,
      basicTextStyle: data.layoutOptions?.basicTextStyle ?? data.style
    }
  }));

export const TextElementDataSchema = RawTextElementDataSchema;

// ObjectFit schema
export const ObjectFitSchema = z.nativeEnum(ObjectFit);

// ImageElementData schema
export const ImageElementDataSchema = z.object({
  source: z.any(), // ImageSource - will need proper schema later
  objectFit: ObjectFitSchema,
  opacity: z.number().min(0).max(1)
});

// Create recursive ElementDecl schema with simplified aliases
// TypeScript limitation: recursive z.lazy schemas require explicit any annotation
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ElementDeclSchema: any = z.lazy(
  () =>
    // Flexible layout properties (width/height can be numbers, objects or missing for auto-fit)
    z
      .object({
        id: ElementIdSchema.optional(),
        // Direct sizing properties (no wrapper) - with proper validation
        width: z.union([SizingAxisSchema, z.number().min(0)]).optional(),
        height: z.union([SizingAxisSchema, z.number().min(0)]).optional(),
        // Other layout properties flattened to element level
        padding: PaddingSchema.optional(),
        childGap: z.number().min(0).optional(),
        direction: LayoutDirectionSchema.optional(),
        childAlignment: ChildAlignmentSchema.optional(),
        // Other element properties
        backgroundColor: SnorfColorSchema.optional(),
        cornerRadius: CornerRadiusSchema.optional(),
        border: BorderElementConfigSchema.optional(),
        textData: TextElementDataSchema.optional(),
        imageData: ImageElementDataSchema.optional(),
        // Visual enhancement properties
        hovered: z.boolean().optional(),
        isSelected: z.boolean().optional(),
        hasCopyOverlay: z.boolean().optional(),
        opacity: z.number().min(0).max(1).optional(),
        clip: z.boolean().optional(),
        zIndex: z.number().optional(),
        children: z.array(ElementDeclSchema).optional()
      })
      .strict()
      .refine(data => {
        // Validate width and height objects explicitly
        if (
          data.width !== undefined &&
          typeof data.width === 'object' &&
          'type' in data.width
        ) {
          const validTypes = ['fit', 'grow', 'percent', 'fixed'];
          if (!validTypes.includes(data.width.type)) {
            throw new Error(
              `Invalid width type: ${data.width.type}. Must be one of: ${validTypes.join(', ')}`
            );
          }
        }

        if (
          data.height !== undefined &&
          typeof data.height === 'object' &&
          'type' in data.height
        ) {
          const validTypes = ['fit', 'grow', 'percent', 'fixed'];
          if (!validTypes.includes(data.height.type)) {
            throw new Error(
              `Invalid height type: ${data.height.type}. Must be one of: ${validTypes.join(', ')}`
            );
          }
        }

        return true;
      })
      .transform(data => {
        // Transform flat properties into layout structure
        const { width, height, padding, childGap, direction, childAlignment, ...rest } =
          data;

        // Transform width and height to sizing format
        const sizing = {
          width:
            width !== undefined
              ? typeof width === 'number'
                ? { type: 'fixed' as const, size: { min: width, max: width } }
                : width
              : {
                  type: 'fit' as const,
                  size: { min: 0, max: Number.MAX_VALUE }
                },
          height:
            height !== undefined
              ? typeof height === 'number'
                ? { type: 'fixed' as const, size: { min: height, max: height } }
                : height
              : {
                  type: 'fit' as const,
                  size: { min: 0, max: Number.MAX_VALUE }
                }
        };

        // Build layout object from flat properties or defaults
        const layout = {
          sizing,
          padding: padding ?? { leading: 0, trailing: 0, top: 0, bottom: 0 },
          childGap: childGap ?? 0,
          direction: direction ?? ('left-to-right' as const),
          childAlignment: childAlignment ?? {
            x: 'left' as const,
            y: 'top' as const
          }
        };

        return {
          ...rest,
          layout
        };
      })
  // ])
);

// SnorfSpec schema - supports nested children
export const SpecSchema = z.object({
  elements: z.array(ElementDeclSchema)
});

export type ElementDeclInput = z.input<typeof ElementDeclSchema>;
export type SpecInput = z.input<typeof SpecSchema>;
